// rtdbMeter — measures how much Realtime Database data each module pulls.
//
// WHY: RTDB egress is billed per GB ($5/GB), and the bill said ~₹1,500/day
// without telling us WHICH screen was responsible. This meter attributes every
// metered read to the sidebar module that was open when it happened, so the
// expensive screen can be found instead of guessed at.
//
// HOW: wrap onValue/get. On each snapshot we measure the payload and add it to
// a per-(module, path) tally. The size is JSON.stringify(...).length, which is
// an APPROXIMATION of bytes on the wire — it ignores protocol overhead and
// counts UTF-16 chars as 1 byte each. It is not a billing figure. It is a
// RANKING tool, and for ranking "which module pulls 180MB vs 2KB" it is exact
// enough to act on.
//
// IMPORTANT: a metered read is still a real read. This file measures; it does
// not reduce anything. Use it to find the offender, then bound the query.

// v2: v1 counted a live listener's FULL snapshot on every delivery and attributed a
// session-wide listener to whatever page was open, which reported 2.5 GB against the
// Dashboard and 958 MB against Quick Order for data neither had read. Bumping the key
// retires those figures instead of leaving people to act on them.
const STORE_KEY = 'daksha.rtdbUsage.v2';
const LEGACY_STORE_KEYS = ['daksha.rtdbUsage.v1'];
const MAX_ENTRIES = 400;          // keep the report small; worst offenders win

/** module key from the current route, e.g. "/customers/123" -> "customers" */
export const currentModule = () => {
    try {
        const p = String(window.location?.pathname || '').replace(/^\/+/, '');
        return p.split('/')[0] || 'dashboard';
    } catch { return 'unknown'; }
};

/** Approximate on-the-wire size of a snapshot value, in bytes. */
export const approxBytes = (value) => {
    if (value == null) return 0;
    try {
        const s = JSON.stringify(value);
        return s ? s.length : 0;
    } catch { return 0; }
};

// tally: { "module\u0000path": { module, path, bytes, reads, lastAt, maxBytes } }
let tally = {};
let startedAt = Date.now();
const listeners = new Set();

const load = () => {
    try {
        // Drop any superseded tally so a stale number cannot linger in a UI.
        LEGACY_STORE_KEYS.forEach(k => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
        const raw = localStorage.getItem(STORE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            tally = parsed.tally || {};
            startedAt = parsed.startedAt || Date.now();
        }
    } catch { /* private mode / blocked storage — meter still works in memory */ }
};
load();

let saveTimer = null;
const save = () => {
    if (saveTimer) return;
    saveTimer = setTimeout(() => {
        saveTimer = null;
        try { localStorage.setItem(STORE_KEY, JSON.stringify({ tally, startedAt })); } catch { /* ignore */ }
    }, 2000);
};

const notify = () => { listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } }); };

/** Record one read. Exported so non-wrapped call sites can report too. */
export const recordRead = (path, bytes, mod = currentModule()) => {
    const b = Number(bytes) || 0;
    const key = `${mod}\u0000${path}`;
    const e = tally[key] || (tally[key] = { module: mod, path, bytes: 0, reads: 0, lastAt: 0, maxBytes: 0 });
    e.bytes += b;
    e.reads += 1;
    e.lastAt = Date.now();
    if (b > e.maxBytes) e.maxBytes = b;

    // Keep the table bounded: when it grows past the cap, drop the cheapest rows.
    const keys = Object.keys(tally);
    if (keys.length > MAX_ENTRIES) {
        keys.sort((x, y) => tally[x].bytes - tally[y].bytes)
            .slice(0, keys.length - MAX_ENTRIES)
            .forEach((k) => { delete tally[k]; });
    }
    save();
    notify();
};

/**
 * A recorder for a LIVE LISTENER, which is not the same thing as a read.
 *
 * `onValue` hands you the whole synced set every time anything inside it changes, but
 * only the CHANGED RECORD crosses the network — the rest is rebuilt from the local
 * cache. Measuring `snapshot.val()` on every callback therefore counts the same data
 * again and again: the orders listener reported 2.5 GB against the Dashboard when the
 * real download was the first sync plus a couple of KB per update.
 *
 * So: the FIRST delivery is charged in full (that one really is downloaded), and every
 * later delivery is recorded as an update — counted, named, but not billed as bytes.
 *
 *   const sync = makeSyncRecorder('/orders (live)');
 *   onValue(ref, snap => { sync(snap.val()); … });
 */
export const makeSyncRecorder = (label, mod) => {
    let first = true;
    return (value) => {
        try {
            if (first) {
                first = false;
                recordRead(`${label} · initial sync`, approxBytes(value), mod || currentModule());
            } else {
                // Zero bytes: the wire carried only the record that changed.
                recordRead(`${label} · live updates`, 0, mod || currentModule());
            }
        } catch { /* metering must never break a read */ }
    };
};

/** Readable path for a Firebase Reference (query params are not included). */
const refPath = (r) => {
    try {
        if (typeof r?.toString === 'function') {
            const s = r.toString();
            const i = s.indexOf('.app', 8);
            const m = s.match(/https?:\/\/[^/]+\/(.*)$/);
            if (m) return '/' + decodeURIComponent(m[1]).replace(/\/$/, '');
            void i;
        }
        return String(r?._path || r?.key || 'unknown');
    } catch { return 'unknown'; }
};

/**
 * Metered onValue. Same signature as firebase/database onValue, plus an
 * optional `label` to group a query under a friendlier name than its path.
 *
 *   const unsub = meteredOnValue(onValue, ordersQuery, cb, { label: 'orders(30d)' })
 *
 * The real `onValue` is passed in so this module never imports the SDK and can
 * be unit-tested without Firebase.
 */
export const meteredOnValue = (onValueImpl, query, cb, opts = {}) => {
    const label = opts.label || refPath(query);
    const mod = opts.module || currentModule();
    return onValueImpl(query, (snap) => {
        try { recordRead(label, approxBytes(snap.val()), mod); } catch { /* never break the read */ }
        cb(snap);
    }, opts.onError);
};

/** Metered get(). Pass the real `get` so this file stays SDK-free. */
export const meteredGet = async (getImpl, query, opts = {}) => {
    const label = opts.label || refPath(query);
    const mod = opts.module || currentModule();
    const snap = await getImpl(query);
    try { recordRead(label, approxBytes(snap.val()), mod); } catch { /* ignore */ }
    return snap;
};

/** The report: per-module totals plus the worst paths inside each. */
export const getUsageReport = () => {
    const rows = Object.values(tally);
    const byModule = {};
    let total = 0;
    rows.forEach((r) => {
        total += r.bytes;
        const m = byModule[r.module] || (byModule[r.module] = { module: r.module, bytes: 0, reads: 0, paths: [] });
        m.bytes += r.bytes;
        m.reads += r.reads;
        m.paths.push(r);
    });
    const modules = Object.values(byModule)
        .map((m) => ({ ...m, paths: m.paths.sort((a, b) => b.bytes - a.bytes).slice(0, 12) }))
        .sort((a, b) => b.bytes - a.bytes);
    return { total, modules, since: startedAt, rows: rows.sort((a, b) => b.bytes - a.bytes) };
};

export const resetUsage = () => {
    tally = {};
    startedAt = Date.now();
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
    notify();
};

/** Subscribe to meter changes (for a live UI). Returns an unsubscribe. */
export const onUsageChange = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
};

export const formatBytes = (n) => {
    const b = Number(n) || 0;
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
